import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const VersionControl = ({ 
  versions, 
  currentVersion, 
  onVersionSelect, 
  onCreateVersion, 
  onPublishVersion,
  onDeleteVersion 
}) => {
  const [showVersionForm, setShowVersionForm] = useState(false);
  const [newVersionData, setNewVersionData] = useState({
    title: '',
    description: '',
    type: 'minor'
  });
  const [selectedVersions, setSelectedVersions] = useState([]);

  const versionTypes = [
    { value: 'major', label: 'Major Update' },
    { value: 'minor', label: 'Minor Update' },
    { value: 'patch', label: 'Patch/Fix' }
  ];

  const statusColors = {
    'draft': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'review': 'bg-blue-100 text-blue-800 border-blue-200',
    'approved': 'bg-green-100 text-green-800 border-green-200',
    'published': 'bg-purple-100 text-purple-800 border-purple-200',
    'archived': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const handleCreateVersion = () => {
    if (newVersionData?.title?.trim()) {
      onCreateVersion({
        id: Date.now()?.toString(),
        ...newVersionData,
        createdAt: new Date(),
        createdBy: 'John Doe',
        status: 'draft',
        changes: []
      });
      setNewVersionData({ title: '', description: '', type: 'minor' });
      setShowVersionForm(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVersionNumber = (index) => {
    const major = Math.floor(index / 10) + 1;
    const minor = index % 10;
    return `v${major}.${minor}`;
  };

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Version Control</h3>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setShowVersionForm(true)}
            iconName="Plus"
            title="Create New Version"
          />
        </div>

        {/* Current Version Info */}
        {currentVersion && (
          <div className="bg-muted/50 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">CURRENT</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                statusColors?.[currentVersion?.status]
              }`}>
                {currentVersion?.status?.toUpperCase()}
              </span>
            </div>
            <h4 className="font-medium text-foreground">{currentVersion?.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">
              {getVersionNumber(0)} • {formatDate(currentVersion?.createdAt)}
            </p>
          </div>
        )}
      </div>
      {/* Create Version Form */}
      {showVersionForm && (
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Version title..."
              value={newVersionData?.title}
              onChange={(e) => setNewVersionData(prev => ({ ...prev, title: e?.target?.value }))}
              className="w-full px-2 py-1 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
            />
            
            <textarea
              placeholder="Description (optional)..."
              value={newVersionData?.description}
              onChange={(e) => setNewVersionData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-2 py-1 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              rows={2}
            />

            <Select
              options={versionTypes}
              value={newVersionData?.type}
              onChange={(value) => setNewVersionData(prev => ({ ...prev, type: value }))}
              placeholder="Select type"
            />

            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="xs"
                onClick={handleCreateVersion}
                disabled={!newVersionData?.title?.trim()}
              >
                Create
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setShowVersionForm(false);
                  setNewVersionData({ title: '', description: '', type: 'minor' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Versions List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="space-y-3">
            {versions?.map((version, index) => (
              <div
                key={version?.id}
                className={`border border-border rounded-lg p-3 cursor-pointer transition-micro hover:shadow-subtle ${
                  currentVersion?.id === version?.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => onVersionSelect(version)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">
                        {getVersionNumber(index)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                        statusColors?.[version?.status]
                      }`}>
                        {version?.status}
                      </span>
                    </div>
                    <h4 className="text-sm font-medium text-foreground line-clamp-2">
                      {version?.title}
                    </h4>
                  </div>

                  <div className="flex items-center space-x-1 ml-2">
                    {version?.status === 'approved' && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onPublishVersion(version?.id);
                        }}
                        iconName="Upload"
                        title="Publish Version"
                        className="p-1"
                      />
                    )}
                    
                    {version?.status === 'draft' && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onDeleteVersion(version?.id);
                        }}
                        iconName="Trash2"
                        title="Delete Version"
                        className="p-1 text-destructive hover:text-destructive"
                      />
                    )}
                  </div>
                </div>

                {version?.description && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {version?.description}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{version?.createdBy}</span>
                  <span>{formatDate(version?.createdAt)}</span>
                </div>

                {version?.changes && version?.changes?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="flex items-center space-x-3 text-xs">
                      <span className="flex items-center space-x-1 text-green-600">
                        <Icon name="Plus" size={10} />
                        <span>{version?.changes?.filter(c => c?.type === 'addition')?.length}</span>
                      </span>
                      <span className="flex items-center space-x-1 text-red-600">
                        <Icon name="Minus" size={10} />
                        <span>{version?.changes?.filter(c => c?.type === 'deletion')?.length}</span>
                      </span>
                      <span className="flex items-center space-x-1 text-blue-600">
                        <Icon name="Edit" size={10} />
                        <span>{version?.changes?.filter(c => c?.type === 'modification')?.length}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {versions?.length === 0 && (
          <div className="text-center py-8 px-4">
            <Icon name="GitBranch" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-2">No versions yet</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVersionForm(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create First Version
            </Button>
          </div>
        )}
      </div>
      {/* Version Actions */}
      <div className="border-t border-border p-4">
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="GitCompare"
            iconPosition="left"
            className="justify-start"
          >
            Compare Versions
          </Button>
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            iconName="Download"
            iconPosition="left"
            className="justify-start"
          >
            Export History
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VersionControl;